// imports
import React, { useState, useEffect, useMemo, use } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Map, ZoomControl, Overlay } from "pigeon-maps"
import { BsInfoSquareFill } from "react-icons/bs";
import { Colors, Spacing } from "_styles";
import { getAirportUuidAndCoordinates, SelectCurrentCoordinates } from "_redux/slices/timeline/timeline-live-location-tab";
import { selectFirebaseInfo } from "_redux/slices/main-screen/user";
import { TbWheelchair } from "react-icons/tb";
import { FaToggleOn, FaToggleOff, FaRegCompass } from "react-icons/fa";

const icons = {
    'Wheelchair-Tracking': TbWheelchair,
    'carts': TbWheelchair,
};

// map providers 
const SATELLITE_PROVIDER = (x, y, z) =>
    `https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

const LABELS_PROVIDER = (x, y, z) =>
    `https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/${z}/${y}/${x}`;

const OSM_PROVIDER = (x, y, z) =>
    `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;


function assetTrackingMap(props) {

    //#region local states
    const [mapCenter, setMapCenter] = useState([0, 0]);                                                                     // default location of map
    const [zoom, setZoom] = useState(18);                                                                                   // default zoom
    const [activeDevice, setActiveDevice] = useState(null);
    const beaconList = props.beaconList;
    const [satelliteView, setSatelliteView] = useState(true);

    //#endregion

    //#region redux
    const dispatch = useDispatch();
    const dbUser = useSelector(selectFirebaseInfo);
    const airportCoordinates = useSelector(SelectCurrentCoordinates);
    //#endregion

    //#region useEffect
    useEffect(() => {
        // return if coordinates are already present
        if (airportCoordinates?.lat && airportCoordinates?.lon) {
            return;
        }
        dispatch(getAirportUuidAndCoordinates(dbUser?.details.authority));
    }, []);

    useEffect(() => {
        if (airportCoordinates?.lat && airportCoordinates?.lon) {
            setMapCenter([parseFloat(airportCoordinates.lat), parseFloat(airportCoordinates.lon)]);
        }
    }, [airportCoordinates]);
    //#endregion

    //#region handlers
    const resetMap = () => {
        if (airportCoordinates?.lat && airportCoordinates?.lon) {
            setMapCenter([parseFloat(airportCoordinates.lat), parseFloat(airportCoordinates.lon)]);                         // reset map center to airport coordinates
            setZoom(18);                                                                                                    // reset zoom to 18
        }
    }

    // map location based on beacon coordinates
    const mapBeaconToCoordinates = (bleItems) => {
        const nearestBleItem = bleItems.reduce((prev, curr) => {
            return (prev.rssi > curr.rssi) ? prev : curr;
        }, { rssi: -1000 });

        
        if (nearestBleItem && nearestBleItem.mac) {
            let matchingBeacon = null;

            for (const beacon of beaconList) {
                if (!beacon) continue;
                
                const mac = nearestBleItem.mac?.toLowerCase();

                if (!mac) continue;

                const macSet = new Set([
                    beacon['macsBleMac'],
                    beacon['macsEddystoneMac'],
                    beacon['macsIbeaconMac'],
                    beacon['macsMainMac'],
                    beacon['mac']
                ].filter(Boolean).map(m => m.toLowerCase()));
                
                if (macSet.has(mac)) {
                    matchingBeacon = beacon;
                    break;
                }
            }
            if (matchingBeacon) {
                let coords = matchingBeacon['currentLocationCoordinates'].split(',');
                if (coords.length == 2) {
                    return {
                        latitude: parseFloat(coords[0]),
                        longitude: parseFloat(coords[1])
                    }
                }
            }
        }
        return null;
    }

    const handleMapView = () => setSatelliteView((prev) => !prev);
    //#endregion

    //#region memos
    // device markers for map
    const deviceMarkers = useMemo(() => {

        const coordMap = {};

        return props.devices?.map((device, index) => {
            let lat = device?.latest?.gpsData?.latitude ?? null;
            let lon = device?.latest?.gpsData?.longitude ?? null;
            let bleItems = device?.latest?.bleItems || [];
            let batteryLow = device?.meta?.batteryLow;
            let lastActive = device?.meta?.deviceLastMotionAt;
            let deviceName = device?.meta?.deviceName;
            let deviceTemp = device?.meta?.deviceTemp;
            let payloadType = device?.latest?.payloadType;
            let eventType = device?.latest?.eventType;
            let operationMode = device?.latest?.operationMode;
            let nearestGateway = device?.meta?.gatewayId;
            let positioningType = device?.latest?.positioningType;
            let rebootReason = device?.latest?.lastRebootReason;
            let stillInMotion = device?.latest?.stillInMotion;
            let positioningFailureReason = device?.latest?.positioningFailureReason;
            const Icon = icons[device?.meta?.deviceType] || TbWheelchair;
            let fPort = device?.latest?.fPort;
            let info = {
                'Device Name': deviceName,
                'Last Movement At': lastActive ? lastActive.toDate().toLocaleString() : 'N/A',
                'Battery Low': batteryLow ? "Yes" : "No",
                'Device Temp': deviceTemp ? `${deviceTemp} °C` : 'N/A',
                'Operation Mode': operationMode || 'N/A',
                'Nearest Gateway': nearestGateway || 'N/A',
            };

            // find device state
            if (payloadType == "heartbeat") {
                info['Last Reboot Reason'] = rebootReason || 'N/A';
                info['Device Shutdown'] = "No";
                info['In Motion'] = stillInMotion ? "Yes" : "No";

                let lastValidPosition = device?.lastValidPosition;

                if (lastValidPosition) {
                    if (lastValidPosition?.positioningType === "gps") {
                        lat = lastValidPosition?.gpsData?.latitude;
                        lon = lastValidPosition?.gpsData?.longitude;
                    }
                    else if (lastValidPosition?.positioningType === "bluetooth") {
                        const coords = mapBeaconToCoordinates(lastValidPosition?.bleItems);
                        if (coords) {
                            lat = coords.latitude;
                            lon = coords.longitude;
                        }
                    }
                }
            }
            else if (payloadType == "location_fixed") {

                console.log("Processing location_fixed for device:", device);
                // find a coordinate if current positioning type is bluetooth
                if (positioningType == "bluetooth") {
                    const coords = mapBeaconToCoordinates(bleItems);
                    if (coords) {
                        lat = coords.latitude;
                        lon = coords.longitude;
                    }

                    // no logic in place if the beacon has no coordinate attached or if found beacon is not in the list
                };
            }
            else if (payloadType == "location_failure") {

                info['Positioning Failure Reason'] = (positioningFailureReason || 'N/A') + ". Showing last known position";

                // check if lastValidPosition is available for the device
                let lastValidPosition = device?.lastValidPosition;

                if (lastValidPosition) {
                    if (lastValidPosition?.positioningType === "gps") {
                        lat = lastValidPosition?.gpsData?.latitude;
                        lon = lastValidPosition?.gpsData?.longitude;
                    }
                    else if (lastValidPosition?.positioningType === "bluetooth") {
                        const coords = mapBeaconToCoordinates(lastValidPosition?.bleItems);
                        if (coords) {
                            lat = coords.latitude;
                            lon = coords.longitude;
                        }
                    }
                }
            }
            else if (payloadType == "shutdown") {
                info['Device Shutdown'] = "Yes";
                info['In Motion'] = "No";

                if (positioningType == "bluetooth") {
                    const coords = mapBeaconToCoordinates(bleItems);
                    if (coords) {
                        lat = coords.latitude;
                        lon = coords.longitude;
                    }
                }
            }
            else if (payloadType == "event") {
                if (eventType == "Start of movement") {
                    info['In Motion'] = "Yes";
                }
                else if (eventType == "End of movement") {
                    info['In Motion'] = "No";
                }

                let lastValidPosition = device?.lastValidPosition;
                if (lastValidPosition) {
                    if (lastValidPosition?.positioningType === "gps") {
                        lat = lastValidPosition?.gpsData?.latitude;
                        lon = lastValidPosition?.gpsData?.longitude;
                    }
                    else if (lastValidPosition?.positioningType === "bluetooth") {
                        const coords = mapBeaconToCoordinates(lastValidPosition?.bleItems);
                        if (coords) {
                            lat = coords.latitude;
                            lon = coords.longitude;
                        }
                    }
                }
            }

            if (!lat || !lon) {
                return null;
            }

            // count overlapping markers
            const key = `${lat.toFixed(6)},${lon.toFixed(6)}`;
            const offsetIndex = coordMap[key] || 0;
            coordMap[key] = offsetIndex + 1;

            lat += 0.00005 * offsetIndex;
            lon += 0.00005 * offsetIndex;

            return (
                <Overlay key={deviceName} anchor={[lat, lon]} offset={[0, 0]}>
                    <div className='device-info'
                        onClick={() => { setActiveDevice(device) }}
                        style={{
                            cursor: 'pointer',
                            transform: 'translate(-50%, -100%)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            pointerEvents: 'auto',
                            position: 'relative',
                        }}
                    >
                        <div className="map-marker" style={{
                            background: payloadType == 'location_failure' ? '#ff4d4d'
                                : payloadType == 'shutdown' ? '#808080'
                                    : payloadType == 'event' && eventType == 'Start of movement' ? '#36ed6dff'
                                        : payloadType == 'event' && eventType == 'End of movement' ? '#f4a3d9ff'
                                            : '#007bff',
                        }}> <Icon className="icon" size={40} color={'white'} /></div>

                        {
                            activeDevice && activeDevice?.meta?.deviceName === deviceName && <div className="plan"
                                onMouseLeave={() => setActiveDevice(null)}>
                                <div className="inner" style={{ backgroundColor: '#ecf0ff' }}>
                                    {
                                        Object.entries(info).map(([key, value]) => (
                                            <p key={key} className="info"><strong>{key}: </strong>{value}</p>
                                        ))
                                    }
                                </div>
                            </div>
                        }
                    </div>
                </Overlay>
            );
        });

    }, [props.devices, activeDevice]);


    const provider = useMemo(
        () => (satelliteView ? SATELLITE_PROVIDER : OSM_PROVIDER),
        [satelliteView]
    );
    //#endregion

    return (
        <>
            <div className='border rounded overflow-hidden bg-white p-3' style={{ height: '100%', width: '100%' }}>
                <div className='bg-primary bg-opacity-10 p-2 mb-3 rounded d-flex align-items-center' style={{ gap: Spacing.spacing2 }}>
                    <strong><BsInfoSquareFill size={Spacing.SCALE_22} /> Shows both indoor and outdoor assets</strong>
                </div>
                <div className='rounded' style={{ height: '80%', width: '100%', position: 'relative' }}>

                    {/* pigeon map */}
                    <Map center={mapCenter}
                        zoom={zoom}
                        onBoundsChanged={({ center, zoom }) => {
                            setMapCenter(center);
                            setZoom(zoom);
                        }}
                        defaultZoom={18}
                        maxZoom={19}
                        minZoom={5}
                        provider={provider}
                    >
                        {/* device markers */}
                        {deviceMarkers}

                        {/* buttons for zooming in and out */}
                        <ZoomControl />
                    </Map>
                </div>
                <div className='m-2 d-flex gap-2' id='mapControls'>
                    <button className='recenterMap' onClick={resetMap}>
                        <FaRegCompass color={'blue'} size={Spacing.SCALE_18} /> Recenter
                    </button>
                    <button
                        className='recenterMap d-flex align-items-center gap-2'
                        style={{ minWidth: 190, whiteSpace: 'nowrap' }}
                        onClick={handleMapView}
                    >
                        {satelliteView ?
                            <FaToggleOn color={'black'} size={Spacing.SCALE_18} /> :
                            <FaToggleOff color={'black'} size={Spacing.SCALE_18} />
                        }
                        {satelliteView ? 'Turn Off Satellite View' : 'Turn On Satellite View'}
                    </button>
                </div>
            </div>
        </>
    )
}


export default assetTrackingMap;